// src/screens/admin/faq/AdminFAQ.jsx
//
// Theme: matches RawMaterialsList — #f5f5f5 bg, white cards, #d67e43 accent
// Prefix: af-

import { useState, useRef, useEffect, useContext } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  HelpCircle,
  Search,
  X,
  ChevronDown,
  Check,
  AlertTriangle,
} from "lucide-react";
import "../../../assets/css/admin/adminFAQ/AdminFAQ.css";
import AppContext from "../../../context/AppContext";
import axios from "axios";

// ─────────────────────────────────────────────────────────────
// SEED DATA  (replace with API call)
// ─────────────────────────────────────────────────────────────
const SEED_FAQS = [
  // {
  //   id: 1,
  //   category: "Orders",
  //   question: "How can I track my order?",
  //   answer:
  //     'Go to "My Orders" and click the order. You\'ll see real-time updates and estimated delivery date.',
  // },
  // {
  //   id: 2,
  //   category: "Orders",
  //   question: "Can I cancel or modify my order?",
  //   answer:
  //     'Cancel within 24 hours via "My Orders" → "Cancel Order". For modifications, contact support.',
  // },
  // {
  //   id: 3,
  //   category: "Payment",
  //   question: "What payment methods do you accept?",
  //   answer:
  //     "Credit/Debit Cards, Cash on Delivery, Bank Transfer, JazzCash, Easypaisa. All transactions SSL encrypted.",
  // },
  // {
  //   id: 4,
  //   category: "Delivery",
  //   question: "What is the delivery time?",
  //   answer:
  //     "Standard 5–7 business days. Karachi/Lahore: 3–5 days. Express 2–3 days available at checkout.",
  // },
  // {
  //   id: 5,
  //   category: "Returns",
  //   question: "What is your return policy?",
  //   answer:
  //     "7-day return policy. Items must be unused in original packaging. Refund in 5–7 business days.",
  // },
  // {
  //   id: 6,
  //   category: "Account",
  //   question: "How do I reset my password?",
  //   answer:
  //     'Settings → Change Password. Or use "Forgot Password" on the login page.',
  // },
  // {
  //   id: 7,
  //   category: "Products",
  //   question: "Are the furniture items customizable?",
  //   answer:
  //     'Look for "Customizable" tag on product pages. Choose fabric, color, and size. Contact support for bulk orders.',
  // },
  // {
  //   id: 8,
  //   category: "Warranty",
  //   question: "Do you offer warranty on furniture?",
  //   answer:
  //     "1-year manufacturer warranty covering defects. Extended warranty available at checkout.",
  // },
  // {
  //   id: 9,
  //   category: "Delivery",
  //   question: "Do you deliver to all cities?",
  //   answer:
  //     "Karachi, Lahore, Islamabad, Rawalpindi, Faisalabad, Multan. Contact support for other cities.",
  // },
  // {
  //   id: 10,
  //   category: "Payment",
  //   question: "Is Cash on Delivery available?",
  //   answer:
  //     "Yes, COD available for most locations. Charges may apply based on order value and area.",
  // },
  // {
  //   id: 11,
  //   category: "Products",
  //   question: "How do I know if a product is in stock?",
  //   answer:
  //     "Availability shown on each product page. Subscribe to restock notifications if out of stock.",
  // },
  // {
  //   id: 12,
  //   category: "Returns",
  //   question: "How long does a refund take?",
  //   answer:
  //     "After receiving and verifying, refunds processed in 5–7 business days to original payment method.",
  // },
];

const CATEGORIES = [
  "Orders",
  "Payment",
  "Delivery",
  "Returns",
  "Account",
  "Products",
  "Warranty",
];
let _nextId = SEED_FAQS.length + 1;

// ─────────────────────────────────────────────────────────────
// CATEGORY DROPDOWN
// ─────────────────────────────────────────────────────────────
const CategoryDropdown = ({
  value,
  onChange,
  includeAll = false,
  placeholder,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const options = includeAll ? ["all", ...CATEGORIES] : CATEGORIES;

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const label =
    value === "all"
      ? placeholder || "All Categories"
      : value || placeholder || "Select category";

  return (
    <div className="af-dd-wrap" ref={ref}>
      <button
        className="af-dd-btn"
        onClick={() => setOpen(!open)}
        type="button"
      >
        <span>{label}</span>
        <ChevronDown
          size={14}
          className={`af-dd-chevron ${open ? "af-dd-chevron-open" : ""}`}
        />
      </button>
      {open && (
        <div className="af-dd-menu">
          {options.map((c) => (
            <button
              key={c}
              className={`af-dd-item ${value === c ? "af-dd-item-active" : ""}`}
              onClick={() => {
                onChange(c);
                setOpen(false);
              }}
              type="button"
            >
              {c === "all" ? "All Categories" : c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// DELETE CONFIRM DIALOG
// ─────────────────────────────────────────────────────────────
const DeleteDialog = ({ faq, isDeleting, onConfirm, onCancel }) => (
  <div className="af-backdrop" onClick={!isDeleting ? onCancel : undefined}>
    <div className="af-modal af-modal-sm" onClick={(e) => e.stopPropagation()}>
      <div className="af-delete-icon-wrap">
        <AlertTriangle size={26} strokeWidth={1.8} />
      </div>
      <h3 className="af-modal-title">Delete FAQ?</h3>
      <p className="af-delete-msg">
        This will permanently remove:
        <br />
        <strong>"{faq.question}"</strong>
      </p>
      <div className="af-modal-actions af-modal-actions-center">
        <button
          className="af-ghost-btn"
          onClick={onCancel}
          disabled={isDeleting}
        >
          Cancel
        </button>
        <button
          className="af-danger-btn"
          onClick={onConfirm}
          disabled={isDeleting}
        >
          {isDeleting ? "Deleting…" : "Yes, Delete"}
        </button>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// ADD / EDIT FORM MODAL
// ─────────────────────────────────────────────────────────────
const FAQFormModal = ({ initial, onSave, onClose, triggerRefresh }) => {
  const { baseUrl, authToken} = useContext(AppContext);
  const [errors, setErrors] = useState({});
  const [category, setCategory] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [idToUpdate, setIdToUpdate] = useState("");

  const addFaq = () => {
    console.log("add faq called!");
    const formData = new FormData();
    formData.append("category", category);
    formData.append("question", question);
    formData.append("answer", answer);
    axios.post(`${baseUrl}/admin/faq/addFaq`, formData, {
      headers:{
        Authorization: `Bearer${authToken}`
      }
    })
    .then((response) => {
      console.log(response.data);
      triggerRefresh();
      onClose();
    })
    .catch((error) => {
      console.log(error);
      alert("network error!");
    })
  }

  const editFaq = () => {
    console.log("edit FAQ called!");
    const formData = new FormData();
    formData.append("id", initial.id);
    formData.append("category", category);
    formData.append("question", question);
    formData.append("answer", answer);
    axios.post(`${baseUrl}/admin/faq/editFaq`, formData, {
      headers: {
         Authorization: `Bearer${authToken}`
      }
    }).then((response) => {
      console.log(response.data);
      triggerRefresh();
      onClose();
    }).catch((error) => {
      console.log(error);
      alert("FAQ not updated!");
    })
  }

  useEffect(() => {
  if (initial) {
    setCategory(initial.category || "");
    setQuestion(initial.question || "");
    setAnswer(initial.answer || "");
    setIdToUpdate(initial.id);
  }
}, [initial]);

  const isEdit = !!initial;

  const set = (key, val) => {
   // setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => {
      const n = { ...e };
      delete n[key];
      return n;
    });
  };

  const validate = () => {
    const e = {};
    if (!question.trim()) e.question = "Question is required";
    if (!answer.trim()) e.answer = "Answer is required";
    return e;
  };

  // const handleSave = () => {
  //   const e = validate();
  //   if (Object.keys(e).length) {
  //     setErrors(e);
  //     return;
  //   }
  //   onSave(form);
  // };

  return (
    <div className="af-backdrop" onClick={onClose}>
      <div className="af-modal" onClick={(e) => e.stopPropagation()}>
        <div className="af-modal-header">
          <h3 className="af-modal-title">
            {isEdit ? "Edit FAQ" : "Add New FAQ"}
          </h3>
          <button
            className="af-icon-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="af-field">
          <label className="af-field-label">Category</label>
          <CategoryDropdown
            value={category}
            onChange={(value) => {
              setCategory(value)
            }}
          />
        </div>

        <div className="af-field">
          <label className="af-field-label">Question</label>
          <input
            className={`af-input ${errors.question ? "af-input-error" : ""}`}
            placeholder="e.g. How do I track my order?"
            value={question}
            onChange={(event) => {
              setQuestion(event.target.value)
            }}
          />
          {errors.question && (
            <span className="af-field-err">{errors.question}</span>
          )}
        </div>

        <div className="af-field">
          <label className="af-field-label">Answer</label>
          <textarea
            className={`af-textarea ${errors.answer ? "af-input-error" : ""}`}
            placeholder="Write a clear, helpful answer..."
            rows={4}
            value={answer}
            onChange={(event) => {
              setAnswer(event.target.value)
            }}
          />
          {errors.answer && (
            <span className="af-field-err">{errors.answer}</span>
          )}
        </div>

        <div className="af-modal-actions">
          <button className="af-ghost-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="af-primary-btn" onClick={isEdit ? editFaq : addFaq}>
            <Check size={14} />
            {isEdit ? "Save Changes" : "Add FAQ"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const AdminFAQ = () => {
  const [faqs, setFaqs] = useState(SEED_FAQS);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const {baseUrl, authToken} = useContext(AppContext);
  const [refresh, setRefresh] = useState(false);

  const deleteFaq = (idToDelete) => {
    console.log("***************");
    console.log("Delete FAQ called!");
    console.log(idToDelete);
    console.log("***************");
    const formData = new FormData();
    formData.append("id", idToDelete);
    axios.post(`${baseUrl}/admin/faq/deleteFaq`, formData, {
      headers: {
         Authorization: `Bearer${authToken}`
      }
    }).then((response) => {
      console.log(response.data);
      viewAllFaq();
      setIsDeleting(false);
      setDeleteTarget(null);
    }).catch((error) => {
      console.log(error);
    })
  }

    const viewAllFaq = () => {
    console.log("view faq called!");
    axios.get(`${baseUrl}/admin/faq/viewAllFaq`, {
      headers: {
         Authorization: `Bearer${authToken}`
      }
    })
    .then((response) => {
      console.log(response.data.faqs);
      setFaqs(response.data.faqs);
    })
    .catch((error) => {
      console.log(error);
    })
  }

  useEffect(() => {
    viewAllFaq();
  },[refresh]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const showToast = (msg, type = "success") => setToast({ msg, type });

  // ── CRUD ──
  const handleAdd = (form) => {
    setFaqs((prev) => [...prev, { ...form, id: _nextId++ }]);
    setShowAdd(false);
    showToast("FAQ added successfully");
  };

  const handleEdit = (form) => {
    setFaqs((prev) =>
      prev.map((f) => (f.id === editTarget.id ? { ...f, ...form } : f)),
    );
    setEditTarget(null);
    showToast("FAQ updated");
  };

  const handleConfirmDelete = () => {
    setIsDeleting(true);
    deleteFaq(deleteTarget.id);
    setTimeout(() => {
      setFaqs((prev) => prev.filter((f) => f.id !== deleteTarget.id));
      setIsDeleting(false);
      setDeleteTarget(null);
      showToast("FAQ deleted", "danger");
    }, 800);
  };

  // ── Filter ──
  const filtered = faqs.filter((f) => {
    const matchCat = filterCat === "all" || f.category === filterCat;
    const q = search.toLowerCase();
    return (
      matchCat &&
      (f.question.toLowerCase().includes(q) ||
        f.answer.toLowerCase().includes(q))
    );
  });

  return (
    <div className="af-container">
      {/* Toast */}
      {toast && (
        <div className={`af-toast af-toast-${toast.type}`}>
          {toast.type === "success" ? (
            <Check size={13} />
          ) : (
            <Trash2 size={13} />
          )}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="af-header">
        <div>
          <h1 className="af-title">FAQ Management</h1>
          <p className="af-subtitle">
            Manage customer frequently asked questions
          </p>
        </div>
        <div className="af-header-actions">
          <button className="af-add-btn" onClick={() => setShowAdd(true)}>
            <Plus size={15} /> Add FAQ
          </button>
        </div>
      </div>

      {/* Stats + Toolbar — same row */}
      <div className="af-stats-toolbar-row">
        {/* Stats card — left side */}
        {/* <div className="af-stats-row">
          <div className="af-stat-card af-stat-total">
            <span className="af-stat-num">{faqs.length}</span>
            <span className="af-stat-label">Total FAQs</span>
          </div>
        </div> */}
        <div className="af-search-wrap">
          <Search size={14} className="af-search-icon" />
          <input
            className="af-search-input"
            placeholder="Search questions or answers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              className="af-search-clear"
              onClick={() => setSearch("")}
              aria-label="Clear"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/* Toolbar — right side: filter left, search right */}
        <div className="af-toolbar">
          <CategoryDropdown
            value={filterCat}
            onChange={setFilterCat}
            includeAll
            placeholder="All Categories"
          />
        </div>
      </div>

      {/* Table */}
      <div className="af-table-container">
        {filtered.length === 0 ? (
          <div className="af-empty">
            <HelpCircle size={40} strokeWidth={1.3} className="af-empty-icon" />
            <h3>No FAQs found</h3>
            <p>
              {search || filterCat !== "all"
                ? "Try different keywords or change the category filter"
                : "Add your first FAQ to get started"}
            </p>
            {(search || filterCat !== "all") && (
              <button
                className="af-add-btn"
                style={{ marginTop: "1rem" }}
                onClick={() => {
                  setSearch("");
                  setFilterCat("all");
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <table className="af-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Category</th>
                <th>Question</th>
                <th>Answer</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((f, idx) => (
                <tr key={f.id}>
                  <td className="af-td-idx">{idx + 1}</td>
                  <td>
                    <span className="af-cat-badge">{f.category}</span>
                  </td>
                  <td className="af-td-question">{f.question}</td>
                  <td className="af-td-answer">{f.answer}</td>
                  <td>
                    <div className="af-actions">
                      <button
                        className="af-action-btn af-action-edit"
                        title="Edit"
                        onClick={() => setEditTarget(f)}
                      >
                        <Edit2 size={13} />
                      </button>
                      <button
                        className="af-action-btn af-action-delete"
                        title="Delete"
                        onClick={() => setDeleteTarget(f)}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {filtered.length > 0 && (
        <p className="af-result-info">
          Showing <strong>{filtered.length}</strong> of{" "}
          <strong>{faqs.length}</strong> FAQs
          {filterCat !== "all" && (
            <>
              {" "}
              · <span className="af-result-cat">{filterCat}</span>
            </>
          )}
        </p>
      )}

      {/* Modals */}
      {showAdd && (
        <FAQFormModal onSave={handleAdd} onClose={() => setShowAdd(false)}
        triggerRefresh={() => setRefresh(prev => !prev)}
         />
      )}
      {editTarget && (
        <FAQFormModal
          initial={editTarget}
          onSave={handleEdit}
          onClose={() => setEditTarget(null)}
          triggerRefresh={()=>setRefresh(prev => !prev)}
        />
      )}
      {deleteTarget && (
        <DeleteDialog
          faq={deleteTarget}
          isDeleting={isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            if (!isDeleting) setDeleteTarget(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminFAQ;
